import { lazy, Suspense, useCallback, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Flex, Loader, Text } from '@mantine/core';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { SigningPanel } from '@dealstack/frontend/shared/components/EidEasySigningModal/SigningPanel';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { WitnessSigningStep } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/WitnessSigningStep';
import { usePublicSigningInfo } from './usePublicSigningInfo';
import { usePublicSigningDocument } from './usePublicSigningDocument';
import { usePublicEidEasySign } from './usePublicEidEasySign';
import { useSaveWitnessDetails } from './useSaveWitnessDetails';
import { useSaveCertifierDetails } from './useSaveCertifierDetails';
import { useUpdateCertificationText } from './useUpdateCertificationText';
import { CertificationTextStep } from './CertificationTextStep';
import { useInviteWitness } from './useInviteWitness';
import { useProgrammeSigningDocuments } from './useProgrammeSigningDocuments';
import { ProgrammeSigningPage } from './ProgrammeSigningPage';
import { PublicSigningHeader } from './PublicSigningHeader';
import {
	buildProgrammePortalPath,
	getProgrammeReturnToken,
	resolveAllowedMethods,
} from './PublicSigningPage.utils';
import { PublicSigningSignedScreen } from './PublicSigningSignedScreen';
import { WitnessDetailsStep } from './WitnessDetailsStep';
import { useQueryClient } from '@tanstack/react-query';
import { isUnauthorizedApiError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { PublicSignerType } from '@dealstack/shared/lib/publicSignerType';
import { BodyRow, PdfCard, SigningPanelCard, StatusScreen } from './PublicSigningPage.styles';

const DocumentViewerV2 = lazy(() =>
	import('@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerV2').then((m) => ({
		default: m.DocumentViewerV2,
	}))
);

const noop = () => undefined;

// The full-height header + centred body used by every pre-content screen
// (loader / error). Kept as a plain helper so the guard cascade below reads as
// one-liners instead of five near-identical blocks.
const renderStatusScreen = (content: ReactNode) => (
	<Flex direction="column" h="100vh">
		<PublicSigningHeader />
		<StatusScreen direction="column" align="center" justify="center" gap="md">
			{content}
		</StatusScreen>
	</Flex>
);

const renderLoaderScreen = () => renderStatusScreen(<Loader size="lg" />);

const renderErrorScreen = (message: string) =>
	renderStatusScreen(
		<>
			<Icon variant="warning" size="3xl" />
			<Text c="red">{message}</Text>
		</>
	);

// Message for the failed-info-query screen: expired links get their own copy,
// everything else the generic failure.
const resolveInfoErrorMessage = (queryError: Error | null, t: (key: string) => string) => {
	const isExpired = (queryError as { message: string }).message === 'LINK_EXPIRED';
	return isExpired ? t('eid_easy_signing.link_expired') : t('eid_easy_signing.signing_failed');
};

// The PDF preview pane: a loader while fetching, the viewer once the blob is in,
// nothing if there's no file.
const renderDocumentPreview = (
	isDocumentLoading: boolean,
	documentBlob: Blob | undefined,
	documentName: string
) => {
	if (isDocumentLoading) {
		return (
			<Flex align="center" justify="center" w="100%">
				<Loader size="lg" />
			</Flex>
		);
	}
	if (!documentBlob) {
		return null;
	}
	return (
		<Suspense
			fallback={
				<Flex align="center" justify="center" w="100%">
					<Loader size="lg" />
				</Flex>
			}
		>
			<DocumentViewerV2 file={documentBlob} fileName={documentName} fileExtension="pdf" />
		</Suspense>
	);
};

export const PublicSigningPage = () => {
	const tCommonUi = useTranslationCommonUi();
	const tMipManage = useTranslationMipManage();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') ?? '';
	const queryClient = useQueryClient();
	const location = useLocation();
	const navigate = useNavigate();

	// Set only when this page was reached from the authorised-signatory portal's
	// per-row Sign action; email-link visitors have no portal to go back to.
	const programmeReturnToken = getProgrammeReturnToken(location.state);

	// The frontend can't decode the token (no secret), so it probes the backend
	// to tell a programme-scoped token (authorised-signatory invites) apart from
	// a signature-scoped one (witness/representative invites, and the portal's
	// per-row token exchange): this endpoint only accepts a programme token, and
	// 401s a signature token.
	const {
		isSuccess: isProgrammeToken,
		isError: isProgrammeProbeError,
		error: programmeProbeError,
	} = useProgrammeSigningDocuments(token);

	// A 401 is the definitive "wrong scope" signal → the token is signature-scoped.
	// Any other failure (transient/network, after the hook's retries) is NOT a
	// signal to treat the token as signature-scoped — doing so would misroute a
	// valid programme visitor — so we surface an error instead (handled below).
	const isSignatureToken = isProgrammeProbeError && isUnauthorizedApiError(programmeProbeError);
	const isProbeFailedTransiently = isProgrammeProbeError && !isSignatureToken;

	const {
		data: signingInfo,
		isLoading,
		error: queryError,
	} = usePublicSigningInfo(token, isSignatureToken);

	const showDocumentPreview = !!signingInfo?.fileId;

	const { data: documentBlob, isLoading: isDocumentLoading } = usePublicSigningDocument(
		token,
		showDocumentPreview
	);

	const inviteWitness = useInviteWitness(token);
	// Set when the signer continues past the witness step; read in the signing
	// completion handler to fire the invite (a ref so the handler never sees a stale value).
	const pendingWitnessEmailRef = useRef<string | null>(null);
	// Retained past dispatch (unlike the ref) so a failed invite can be retried.
	const [nominatedWitnessEmail, setNominatedWitnessEmail] = useState<string | null>(null);
	// Drives the retry affordance on the signed screen. Without it a failed invite is
	// silent: the signer sees success while the witness never hears anything.
	const [witnessInviteFailed, setWitnessInviteFailed] = useState(false);
	// Unlocks the signing panel once the witness step is completed.
	const [witnessReady, setWitnessReady] = useState(false);

	const sendWitnessInvite = useCallback(
		(email: string) => {
			setWitnessInviteFailed(false);
			inviteWitness.mutate(email, { onError: () => setWitnessInviteFailed(true) });
		},
		[inviteWitness]
	);

	const {
		status,
		challengeCode,
		error: signingError,
		selectedMethod: signingSelectedMethod,
		goBack,
		handleSelectMethod,
		handleConfirmSmartId,
		handleConfirmSignature,
		handleSignatureConfirmed,
		handleConfirmFreja,
		handleConfirmOtp,
		handleSubmitOtpCode,
		handleResendOtpCode,
		otpVerifying,
		otpCodeError,
		otpChannel,
		otpMaskedContact,
	} = usePublicEidEasySign({
		token,
		onComplete: () => {
			const pendingWitnessEmail = pendingWitnessEmailRef.current;
			if (pendingWitnessEmail) {
				pendingWitnessEmailRef.current = null;
				sendWitnessInvite(pendingWitnessEmail);
			}

			queryClient.setQueryData(['public-signing-info', token], (prev: typeof signingInfo) =>
				prev ? { ...prev, canSign: false, signatureStatus: 'SIGNED' } : prev
			);
			// Refetch so the now-populated signer/method/session fields load for the
			// success confirmation (the optimistic patch above only flips canSign).
			void queryClient.invalidateQueries({ queryKey: ['public-signing-info', token] });
			// The preview cached the unsigned PDF forever (staleTime: Infinity).
			// Remove it so the download button refetches the signed PDF. Remove,
			// not invalidate, so there's no stale blob to download while it refetches.
			void queryClient.removeQueries({ queryKey: ['public-signing-document', token] });
		},
	});

	const retryWitnessInvite = useCallback(() => {
		if (nominatedWitnessEmail) {
			sendWitnessInvite(nominatedWitnessEmail);
		}
	}, [nominatedWitnessEmail, sendWitnessInvite]);

	const saveWitnessDetails = useSaveWitnessDetails(token);
	const saveCertifierDetails = useSaveCertifierDetails(token);
	const updateCertificationText = useUpdateCertificationText(token);
	const [isEditingWitnessDetails, setIsEditingWitnessDetails] = useState(false);
	// DC-825: a certifier confirms the certification wording before the signing panel unlocks.
	const [isCertificationConfirmed, setIsCertificationConfirmed] = useState(false);

	// Shown above the document (and on the signed confirmation) so a signatory
	// working through a programme can return to the table for the next one.
	const backToPortalButton = programmeReturnToken ? (
		<Button
			variant="default"
			leftSection={<Icon variant="arrow-left" size="md" />}
			onClick={() => navigate(buildProgrammePortalPath(programmeReturnToken))}
		>
			{tMipManage('documents_page.programme_signing.back_button', {
				defaultValue: 'Back to your documents',
			})}
		</Button>
	) : null;

	const allowedMethods = resolveAllowedMethods(signingInfo);

	if (!token) {
		return renderErrorScreen(tCommonUi('eid_easy_signing.signing_failed'));
	}

	if (isProgrammeToken) {
		return <ProgrammeSigningPage />;
	}

	// Probe not resolved yet (mutually exclusive with isProgrammeToken above), or
	// the info query still loading — show the shared spinner rather than flash the
	// single-doc UI. When the probe is unresolved the info query is disabled, so
	// isLoading is false there.
	const isProbePending = !isProgrammeToken && !isProgrammeProbeError;
	if (isProbePending || isLoading) {
		return renderLoaderScreen();
	}

	if (isProbeFailedTransiently) {
		// The probe failed with a non-401 (transient) error, so we can't tell which
		// kind of token this is. Show a recoverable error rather than guessing —
		// and crucially, don't enable the single-document flow, which keys off
		// isSignatureToken and would otherwise fire spurious requests.
		return renderErrorScreen(tCommonUi('eid_easy_signing.signing_failed'));
	}

	if (queryError || !signingInfo) {
		return renderErrorScreen(resolveInfoErrorMessage(queryError, tCommonUi));
	}

	if (!signingInfo.canSign) {
		return (
			<PublicSigningSignedScreen
				signingInfo={signingInfo}
				documentBlob={documentBlob}
				isDocumentLoading={isDocumentLoading}
				backToPortalButton={backToPortalButton}
				witnessInviteFailed={witnessInviteFailed}
				isRetryingWitnessInvite={inviteWitness.isPending}
				onRetryWitnessInvite={retryWitnessInvite}
			/>
		);
	}

	// A witness must supply their legal details before the signing panel unlocks.
	// Once saved they can still reopen the prefilled form to correct a mistake,
	// right up until they sign.
	const witnessDetailsSaved = !!signingInfo.witnessDetails;
	const showWitnessDetailsForm =
		signingInfo.requiresWitnessDetails && (!witnessDetailsSaved || isEditingWitnessDetails);

	// A representative whose signature requires a witness must nominate one before the
	// signing panel unlocks; the invite is sent on completion (mirrors the investor flow).
	const showWitnessNominationForm = signingInfo.requiresWitnessNomination && !witnessReady;

	const isCertifier = signingInfo.signerType === PublicSignerType.Certifier;
	const saveSignerDetails = isCertifier ? saveCertifierDetails : saveWitnessDetails;

	// For a witness the signing panel's "Back" returns to their details form (to
	// correct a mistake), and for a certifier to the certification wording; everyone
	// else steps back within the signing flow. We reset the signing state too so
	// re-entry starts clean.
	const handleSigningGoBack = () => {
		goBack();
		if (signingInfo.signerType === PublicSignerType.Witness) {
			setIsEditingWitnessDetails(true);
		}
		if (isCertifier) {
			setIsCertificationConfirmed(false);
		}
	};

	const renderPanelBody = () => {
		if (showWitnessDetailsForm) {
			return (
				<WitnessDetailsStep
					defaultValues={signingInfo.witnessDetails}
					isSubmitting={saveSignerDetails.isPending}
					hasError={saveSignerDetails.isError}
					isCertifier={isCertifier}
					onSubmit={(details) =>
						saveSignerDetails.mutate(details, {
							onSuccess: () => setIsEditingWitnessDetails(false),
						})
					}
					onCancel={
						witnessDetailsSaved ? () => setIsEditingWitnessDetails(false) : undefined
					}
				/>
			);
		}

		if (signingInfo.certification && !isCertificationConfirmed) {
			return (
				<CertificationTextStep
					certification={signingInfo.certification}
					isUpdating={updateCertificationText.isPending}
					hasUpdateError={updateCertificationText.isError}
					onUpdate={(text) => updateCertificationText.mutate(text)}
					onContinue={() => setIsCertificationConfirmed(true)}
					onEditDetails={() => setIsEditingWitnessDetails(true)}
				/>
			);
		}

		if (showWitnessNominationForm) {
			return (
				<WitnessSigningStep
					onContinue={(witnessEmail) => {
						pendingWitnessEmailRef.current = witnessEmail;
						setNominatedWitnessEmail(witnessEmail);
						setWitnessReady(true);
					}}
				/>
			);
		}

		return (
			<SigningPanel
				status={status}
				challengeCode={challengeCode}
				error={signingError}
				selectedMethod={signingSelectedMethod}
				allowedMethods={allowedMethods}
				disableSavedSignature
				onClose={noop}
				onSelectMethod={handleSelectMethod}
				onGoBack={handleSigningGoBack}
				onConfirmSmartId={handleConfirmSmartId}
				onConfirmSignature={handleConfirmSignature}
				onSignatureConfirmed={(sig) => {
					void handleSignatureConfirmed(sig);
				}}
				onConfirmFreja={handleConfirmFreja}
				onConfirmOtp={(channel, value) => {
					void handleConfirmOtp(channel, value);
				}}
				onSubmitOtpCode={(code) => {
					void handleSubmitOtpCode(code);
				}}
				onResendOtpCode={handleResendOtpCode}
				otpVerifying={otpVerifying}
				otpCodeError={otpCodeError}
				otpChannel={otpChannel}
				otpMaskedContact={otpMaskedContact}
			/>
		);
	};

	return (
		<Flex direction="column" h="100vh">
			<PublicSigningHeader />

			<Flex align="center" gap="md" px="xl" py="md">
				{backToPortalButton}
				<Text fw={600} size="xl">
					{signingInfo.certification
						? tCommonUi('eid_easy_signing.certification.page_title', {
								defaultValue: 'Certify a document for {{investorFullName}}',
								investorFullName: signingInfo.certification.investorFullName,
							})
						: signingInfo.documentName}
				</Text>
			</Flex>

			<BodyRow>
				{showDocumentPreview && (
					<PdfCard>
						{renderDocumentPreview(
							isDocumentLoading,
							documentBlob,
							signingInfo.documentName
						)}
					</PdfCard>
				)}

				<SigningPanelCard data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_PANEL_PANE}>
					{renderPanelBody()}
				</SigningPanelCard>
			</BodyRow>
		</Flex>
	);
};
